import { useRef } from 'react';
import { Box, Container, Typography, Chip } from '@mui/material';
import { landingColors, landingFont, eyebrowSx, sectionHeadingSx } from '../landingTheme';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { getLandingIcon } from '../landingIcons';
import { EXPERIENCE_STEPS, DASHBOARD_STATS, DASHBOARD_TAGS } from '../landingContent';

export default function ExperienceSection() {
  const sectionRef = useRef(null);
  useScrollReveal(sectionRef);

  return (
    <Box
      ref={sectionRef}
      component="section"
      id="customer-section"
      aria-label="Customer Experience"
      sx={{ py: { xs: 8, md: 14 }, backgroundColor: landingColors.paperAlt }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 4 } }}>
        <Box sx={{ textAlign: 'center', maxWidth: 720, mx: 'auto', mb: { xs: 5, md: 8 } }}>
          <Typography data-reveal sx={{ ...eyebrowSx, color: landingColors.indigo, fontFamily: landingFont, mb: 1.5 }}>
            Customer Experience
          </Typography>
          <Typography data-reveal data-reveal-delay="0.06" component="h2" sx={{ ...sectionHeadingSx, fontFamily: landingFont, color: landingColors.ink, mb: 2 }}>
            A seamless dining experience
          </Typography>
          <Typography data-reveal data-reveal-delay="0.12" sx={{ fontFamily: landingFont, color: landingColors.inkMuted, fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.6 }}>
            Frictionless from the moment guests sit down to the moment their food arrives.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: { xs: 2.5, md: 3 }, mb: { xs: 5, md: 8 } }}>
          {EXPERIENCE_STEPS.map((step, index) => {
            const Icon = getLandingIcon(step.icon);
            return (
              <Box
                key={step.title}
                data-reveal
                data-reveal-delay={(index * 0.08).toString()}
                sx={{
                  p: { xs: 3, md: 3.5 },
                  borderRadius: '20px',
                  backgroundColor: landingColors.paper,
                  border: `1px solid ${landingColors.line}`,
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: landingColors.indigoSoft,
                    color: landingColors.indigo,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  <Icon sx={{ fontSize: 28 }} />
                </Box>
                <Typography sx={{ fontFamily: landingFont, fontWeight: 800, color: landingColors.indigo, fontSize: '0.85rem', mb: 0.5 }}>
                  {`STEP ${index + 1}`}
                </Typography>
                <Typography component="h3" sx={{ fontFamily: landingFont, fontWeight: 700, fontSize: '1.05rem', color: landingColors.ink, mb: 1 }}>
                  {step.title}
                </Typography>
                <Typography sx={{ fontFamily: landingFont, color: landingColors.inkMuted, fontSize: '0.88rem', lineHeight: 1.6 }}>
                  {step.description}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Box
          data-reveal
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 4, md: 6 },
            p: { xs: 3, md: 5 },
            borderRadius: '24px',
            backgroundColor: landingColors.indigo,
            color: '#fff',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography component="h3" sx={{ fontFamily: landingFont, fontWeight: 800, fontSize: { xs: '1.5rem', md: '2rem' }, mb: 2 }}>
              Your dashboard, your command center
            </Typography>
            <Typography sx={{ fontFamily: landingFont, fontSize: { xs: '0.95rem', md: '1.1rem' }, lineHeight: 1.7, opacity: 0.92, mb: 3 }}>
              Every order flows into one centralized dashboard in real time. Run a Kanban board, view kitchen
              displays, manage payments, and track analytics, all from one place.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {DASHBOARD_TAGS.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{
                    fontFamily: landingFont,
                    backgroundColor: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    fontWeight: 600,
                    border: '1px solid rgba(255,255,255,0.28)',
                  }}
                />
              ))}
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: { xs: 2, md: 2.5 } }}>
            {DASHBOARD_STATS.map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                  borderRadius: '16px',
                  p: { xs: 2, md: 2.5 },
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontFamily: landingFont, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.4rem' }, lineHeight: 1, mb: 0.5 }}>
                  {stat.value}
                </Typography>
                <Typography sx={{ fontFamily: landingFont, opacity: 0.85, fontSize: { xs: '0.78rem', md: '0.88rem' } }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
